import { DeviceComponent, extractDeviceComponentsFromStackInfo, deviceComponentsMatch } from './hardware-description';
import { satisfies, RangeOptions, rcompare, valid } from 'semver';
import { Device } from './device';

export interface Firmware {
  /**
   * @example package_SOMANET-Servo-Node-1000-E-EtherCAT-Safety_8432-03_motion-drive_v4.2.0-alpha.5.zip
   */
  packageFilename: string;

  /**
   * @example v4.2.0-alpha.5
   */
  version: string;
}

export const firmwareIdRegExp = /\d{4}-\d{2}(-\d{4})?(-\d+)*/;
export const newFirmwarePackageFilenameRegExp = /package_(.*).zip/;
export const oldFirmwarePackageFilenameRegExp = /package-motion-drive_(.*).zip/;

/**
 * New firmware package filenames are described in section 4.3.3 of the "Hardware Description File Format".
 * For more details, refer to the document at: {@link https://docs.google.com/document/d/1bafY-F8qFMLd0Las8fQFF6H9PTVYFjv01bhV7owqx8E}.
 *
 * @example
 * "package_SOMANET-Servo-Node-400-EtherCAT_9501-01-4622-1_motion-drive_v4.1.0.zip"
 */
export interface NewFirmwarePackageFilename {
  /**
   * General format description: "package" refers to the zip bundles used to deliver firmware for SOMANET devices.
   */
  description: string;

  /**
   * Human-readable hardware name, e.g., "SOMANET-Servo-Node-400-EtherCAT".
   */
  hardwareName: string;

  /**
   * {@link FirmwareId}
   */
  firmwareId: string;

  /**
   * Name of the firmware (SOMANET Software).
   *
   * @example
   * "motion-drive"
   */
  firmwareName: string;

  /**
   * Version of the firmware (SOMANET Software).
   *
   * @example
   * "v4.1.0"
   */
  firmwareVersion: string;

  // Optional properties extracted from the firmware filename as {@link FirmwareId}, applicable only to SOMANET firmware.
  productId?: number;
  productVersion?: number;
  keyId?: number;
  fieldbusProtocol?: number;
}

/**
 * Documentation for old firmware package filenames is not available.
 *
 * @example
 * "package-motion-drive_ComEtherCAT-b_CoreC2X-a_Drive400-e_v4.2.1.zip"
 */
export interface OldFirmwarePackageFilename {
  description: string;
  components: DeviceComponent[];
  firmwareVersion: string;
}

/**
 * This is referred to as the Firmware ID (FWID).
 *
 * The firmware ID consists of the following components:
 * - Product ID (mandatory) – For example, 9501. Sourced from the Assembly or Device section in the `.hardware_description` file.
 * - Product Version (mandatory) – For example, 01. Sourced from the Assembly or Device section in the `.hardware_description` file.
 * - Key ID (optional) – For example, 4622. Sourced from the Device section in the `.hardware_description` file.
 * - Fieldbus Protocol (optional) – For example, 1 for EtherCAT. Sourced from the `.variant` file.
 *
 * @example
 * "9501-01-4622-1"
 */
export interface FirmwareId {
  productId: number;
  productVersion: number;
  keyId?: number;
  fieldbusProtocol?: number;
}

/**
 * Parses the FWID string and returns a {@link FirmwareId} instance.
 *
 * Special cases:
 * - For product IDs `9506`, `9507`, `9508`, `9606`, `9607`, `9608`, `9612`, and `9614`,
 *   the **CAN** fieldbus protocol is always used, regardless of whether another protocol
 *   is specified in the FWID string.
 *
 * @throws {Error} If the provided FWID does not match the expected pattern.
 */
export function parseFirmwareId(fwid: string): FirmwareId | void {
  if (!firmwareIdRegExp.test(fwid)) {
    console.warn(`The provided FWID "${fwid}" does not match the expected pattern.`);
    return;
  }

  // Split the FWID string into its dash-separated components.
  const parts = fwid.split('-');

  // Parse the first three components: productId, productVersion, and keyId.
  const [productId, productVersion, keyId] = parts.slice(0, 3).map((part) => parseInt(part));

  // Parse the optional fieldbusProtocol component (if provided).
  // This value is encoded in hexadecimal.
  let fieldbusProtocol: number | undefined = undefined;
  if (parts[3]) {
    fieldbusProtocol = parseInt(parts[3], 16);
  }

  // Special case for CAN-based devices.
  const canProductIds = [9506, 9507, 9508, 9606, 9607, 9608, 9612, 9614];
  if (canProductIds.includes(productId)) {
    fieldbusProtocol = 3; // CAN
  }

  return { productId, productVersion, keyId, fieldbusProtocol };
}

/**
 * The format for new firmware package filenames is detailed in the "Hardware Description File Format" document.
 */
export function isNewFirmwarePackageFilename(filename: string) {
  return newFirmwarePackageFilenameRegExp.test(filename);
}

/**
 * Documentation for older firmware package filenames, such as "package-motion-drive_ComEtherCAT-b_CoreC2X-a_Drive400-e_v4.2.1.zip", is unavailable.
 * These package filenames consist of three hardware components (modules): Com, Core, and Drive.
 * Firmware packages with these older naming conventions are specifically built for legacy SOMANET Node devices.
 */
export function isOldFirmwarePackageFilename(filename: string) {
  return oldFirmwarePackageFilenameRegExp.test(filename);
}

/**
 * Parses a new firmware package filename and returns an instance of {@link NewFirmwarePackageFilename}.
 * Returns `undefined` if the filename does not match the new firmware package filename format.
 *
 * @throws {Error} Throws an error if the provided filename does not match the expected pattern.
 */
export function parseNewFirmwarePackageFilename(filename: string): NewFirmwarePackageFilename {
  if (!isNewFirmwarePackageFilename(filename)) {
    throw new Error(`The provided filename "${filename}" does not match the expected pattern.`);
  }

  // 'package_SOMANET-Integro-60_9002-02_motion-drive_v5.4.0-rc.3 (1).zip' -> 'package_SOMANET-Integro-60_9002-02_motion-drive_v5.4.0-rc.3.zip'
  const filenameNoWindowsNumbering = filename.replace(/\s\(\d+\)/, '');
  // 'package_SOMANET-Integro-60_9002-02_motion-drive_v5.4.0-rc.3.zip' -> 'package_SOMANET-Integro-60_9002-02_motion-drive_v5.4.0-rc.3'
  const filenameNoExt = filenameNoWindowsNumbering.replace(/\.[^/.]+$/, '');
  const [description, hardwareName, firmwareId, firmwareName, firmwareVersion] = filenameNoExt.split('_');

  // Attempt to fix firmware version if it contains spaces instead of a plus sign for build metadata.
  // Matthias reported that some customers encountered issues installing package files due to spaces in the version.
  // The firmware version in the package file name does not conform to the valid SemVer specification.
  const fixedFirmwareVersion = firmwareVersion.replace(' ', '+');

  return {
    description,
    hardwareName,
    firmwareId,
    firmwareName,
    firmwareVersion: fixedFirmwareVersion,
    ...parseFirmwareId(firmwareId),
  };
}

/**
 * Given an old firmware package filename in the format:
 * "package-motion-drive_ComEtherCAT-b_CoreC2X-a_Drive400-e_v4.2.1.zip",
 * this function extracts the hardware components and returns an array of objects.
 * Each object includes the component name, version, and an empty serial number.
 *
 * This function is useful for determining the compatibility of a firmware package with devices that use an old `stack_info.json` file,
 * which includes only hardware components but lacks a firmware ID. To check if two sets of components match and verify compatibility,
 * use the {@link deviceComponentsMatch} function.
 *
 * @throws {Error} Throws an error if the provided filename does not match the expected pattern.
 */
export function extractDeviceComponentsFromOldFirmwarePackageFilename(filename: string): DeviceComponent[] {
  if (!isOldFirmwarePackageFilename(filename)) {
    throw new Error(`The provided filename "${filename}" does not match the expected pattern.`);
  }

  if (filename.match(/_Com[^_]+_Core[^_]+_Drive/g)) {
    return filename
      .split('_')
      .slice(1, 4)
      .map((b) => {
        // b = ['ComEtherCAT-b', 'CoreC2X-a', 'Drive400-d']
        const p = b.split('-'); // p = ['ComEtherCAT', 'b']
        return {
          name: p[0],
          serialNumber: '',
          version: p[1],
        } as DeviceComponent;
      });
  }

  return [];
}

/**
 * Parses an old firmware package filename and returns an instance of {@link OldFirmwarePackageFilename}.
 * Returns `undefined` if the filename does not match the old firmware package filename format.
 *
 * @throws {Error} Throws an error if the provided filename does not match the expected pattern.
 */
export function parseOldFirmwarePackageFilename(filename: string): OldFirmwarePackageFilename {
  if (!isOldFirmwarePackageFilename(filename)) {
    throw new Error(`The provided filename "${filename}" does not match the expected pattern.`);
  }

  const parts = filename.split('_');
  const components = extractDeviceComponentsFromOldFirmwarePackageFilename(filename);
  return {
    description: parts[0],
    components,
    firmwareVersion: parts?.at(-1)?.replace('.zip', '') ?? '',
  };
}

/**
 * Checks if a firmware package filename is compatible with a device, based on the provided FWID or a device object with a Hardware Description and optionally an Integro Variant.
 * This function helps determine whether a firmware package can be installed on a device.
 *
 * - If the device includes Stack Info, the old firmware package filename is used.
 * - If an FWID or a device with a Hardware Description is provided, the new firmware package filename is used.
 *
 * @param filename The firmware package filename (either old or new).
 * @param firmwareIdOrDevice The FWID or device object (with `hardwareDescription`, `stackInfo`, and optionally `integroVariant`) to check compatibility.
 * @returns `true` if the firmware package filename is compatible with the device; otherwise, `false`.
 * @throws {Error} Throws an error if an invalid combination is provided, such as using an old firmware package filename with an FWID.
 */
export function isFirmwarePackageFilenameCompatibleWithDevice(
  filename: string,
  firmwareIdOrDevice: string | Pick<Device, 'hardwareDescription' | 'stackInfo' | 'integroVariant'>
): boolean {
  if (isOldFirmwarePackageFilename(filename)) {
    if (typeof firmwareIdOrDevice === 'string') {
      throw new Error(
        'The old firmware package filename must be checked against the Stack Info for compatibility verification, not the FWID.'
      );
    } else if (!firmwareIdOrDevice.stackInfo) {
      throw new Error(
        'The old firmware package filename must be checked against the Stack Info for compatibility verification, not the Hardware Description.'
      );
    }
  }

  if (
    isNewFirmwarePackageFilename(filename) &&
    typeof firmwareIdOrDevice !== 'string' &&
    !firmwareIdOrDevice.hardwareDescription
  ) {
    throw new Error(
      'The new firmware package filename must be checked against the FWID or Hardware Description for compatibility verification, not the Stack Info.'
    );
  }

  if (typeof firmwareIdOrDevice === 'string') {
    return firmwareIdOrDevice === extractFirmwareId(filename);
  } else if (firmwareIdOrDevice.stackInfo) {
    const firmwarePackageFilenameDeviceComponents = extractDeviceComponentsFromOldFirmwarePackageFilename(filename);
    const stackInfoDeviceComponents = extractDeviceComponentsFromStackInfo(firmwareIdOrDevice.stackInfo);
    return deviceComponentsMatch(firmwarePackageFilenameDeviceComponents, stackInfoDeviceComponents);
  } else {
    const firmwarePackageFilenameFirmwareId = extractFirmwareId(filename);
    const hardwareDescriptionFirmwareId = extractFirmwareId(firmwareIdOrDevice);
    return firmwarePackageFilenameFirmwareId === hardwareDescriptionFirmwareId;
  }
}

/**
 * Extracts the Firmware ID (FWID) from either the new firmware package filename or the provided Hardware Description.
 * The optional Integro Variant is used if the Firmware ID includes the Fieldbus Protocol.
 *
 * Note: If additional options from the Integro Variant are incorporated into the FWID in the future, this code will need to be updated accordingly.
 *
 * Note: When a Hardware Description (HD) is provided, the FWID is assembled as follows:
 * - If no assembly is present in the HD, the ID, version, and key ID are taken from the device.
 * - If an assembly is present, the ID and version are taken from the assembly, while the key ID is still taken from the device.
 * - In all cases, the key ID is sourced from the device.
 *
 * @param firmwarePackageFilenameOrDevice The new firmware package filename or a device containing a Hardware Description, with an optional Integro Variant.
 * @throws {Error} If the provided filename does not match the expected pattern.
 * @returns The Firmware ID (FWID), e.g., "9501-01-4622-1".
 */
export function extractFirmwareId(
  firmwarePackageFilenameOrDevice: string | Pick<Device, 'hardwareDescription' | 'integroVariant'>
): string {
  if (typeof firmwarePackageFilenameOrDevice === 'string') {
    const firmwarePackageFilename = firmwarePackageFilenameOrDevice;
    if (isNewFirmwarePackageFilename(firmwarePackageFilename)) {
      const obj = parseNewFirmwarePackageFilename(firmwarePackageFilename);
      return obj?.firmwareId;
    } else {
      throw new Error(`The provided filename "${firmwarePackageFilename}" does not match the expected pattern.`);
    }
  } else {
    const device = firmwarePackageFilenameOrDevice;

    let fwid = device.hardwareDescription?.assembly
      ? `${device.hardwareDescription?.assembly.id}-${device.hardwareDescription?.assembly.version}`
      : `${device.hardwareDescription?.device.id}-${device.hardwareDescription?.device.version}`;

    if (device.hardwareDescription?.device.keyId) {
      fwid += `-${device.hardwareDescription?.device.keyId}`;
    }

    if (device.integroVariant) {
      const ids = device.integroVariant.selectedOptionId['fieldbusProtocol'];
      let fieldbusProtocol = ids[0];
      if (ids.length > 1 && ids.includes(1)) {
        fieldbusProtocol = 1; // EtherCAT is preferred if multiple protocols are selected.
      }
      fwid += `-${fieldbusProtocol}`;
    }

    return fwid;
  }
}

/**
 * Extracts the firmware version from the given firmware package filename, whether it's in the old or new format.
 *
 * @example
 * "v4.1.0"
 *
 * @throws {Error} Throws an error if the provided filename does not match the expected pattern.
 */
export function extractFirmwareVersionFromFirmwarePackageFilename(filename: string): string {
  if (isNewFirmwarePackageFilename(filename)) {
    const parsed = parseNewFirmwarePackageFilename(filename);
    return parsed.firmwareVersion;
  } else if (isOldFirmwarePackageFilename(filename)) {
    const parsed = parseOldFirmwarePackageFilename(filename);
    return parsed.firmwareVersion;
  } else {
    throw new Error(`The provided filename "${filename}" does not match the expected pattern.`);
  }
}

/**
 * Older firmware versions for Circulo included a different build to support the Encoder Calibration feature in order to save memory.
 * Newer firmware versions now include the Encoder Calibration feature, so a separate build is no longer necessary.
 *
 * @example
 * "package_SOMANET-Servo-Node-Circulo-7-700_8502-01_motion-drive_v0.4.3-calibration.zip"
 * "v0.4.3-calibration"
 */
export function isCalibrationFirmware(version?: string): boolean {
  return version?.includes('calibration') ?? false;
}

/**
 * Some older firmware versions included invalid build metadata.
 * This function removes any invalid metadata from the provided version.
 */
export function removeInvalidBuildMetadataFromFirmwareVersion(version: string): string {
  return version.replace(/-\d+-[A-Za-z0-9]+$/, '');
}

/**
 * Checks if a firmware version satisfies the given range.
 * If no options are specified, a custom one will be used.
 * Older firmware versions sometimes included invalid build metadata, which could cause the function to fail.
 * To prevent this, the function removes any invalid build metadata before performing the version check.
 */
export function firmwareVersionSatisfies(
  version: string,
  range: string,
  options: RangeOptions = { includePrerelease: true, loose: true }
): boolean {
  version = removeInvalidBuildMetadataFromFirmwareVersion(version);
  return satisfies(version, range, options);
}

/**
 * Determines if a firmware version falls within a specified range.
 *
 * @param version The firmware version to check.
 * @param range A version range, following the syntax outlined in {@link https://github.com/npm/node-semver#ranges}.
 * @param calibration If a string, it will be treated as a range. Otherwise, the function returns the boolean value of `calibration`.
 */
export function isFirmwareVersionInRange(
  version: string,
  range?: string | null,
  calibration?: boolean | string
): boolean {
  let result = false;

  if (isCalibrationFirmware(version)) {
    result = typeof calibration === 'string' ? firmwareVersionSatisfies(version, calibration) : calibration ?? false;
  } else if (range) {
    result = firmwareVersionSatisfies(version, range);
  }

  return result;
}

/**
 * Checks whether a firmware version is Jasper, the firmware the Circulo NG boards run.
 *
 * Jasper versions start at v10.0.0, chosen so that they never collide with SOMANET Software v5.6.x and so
 * that the version alone says which firmware a drive is running without having to name it. That makes the
 * version the right thing to key firmware differences off: it covers every product that runs Jasper,
 * including the Custom Projects boards, where a product ID range would only cover the ones it lists.
 *
 * The lower bound is a prerelease so that a v10.0.0 release candidate is recognised too — `>=v10.0.0`
 * alone excludes `v10.0.0-rc.1`, because a prerelease sorts below the release it leads to.
 */
export function isJasperFirmware(version?: string): boolean {
  if (!version) {
    return false;
  }
  return firmwareVersionSatisfies(version, '>=v10.0.0-0');
}

/**
 * Checks if the provided firmware version includes the new encoder configuration objects (0x2110 and 0x2112).
 * Firmware versions starting from v5.0.0-rc.5 and v0.4.0-calibration include these objects.
 *
 * Previously, firmware had separate objects for each type of encoder (sensor), resulting in more than 12 record objects,
 * each with several subitems. This consumed unnecessary memory, prompting a refactor to introduce the new encoder configuration objects.
 *
 * The new configuration is referred to as "encoder configuration" (0x2110, 0x2112), while the older is called "sensor configuration".
 */
export function isEncoderConfigurationFirmware(version?: string): boolean {
  if (!version) {
    return false;
  }
  if (isCalibrationFirmware(version)) {
    return firmwareVersionSatisfies(version, '>=v0.4.0-calibration');
  } else {
    return firmwareVersionSatisfies(version, '>=v5.0.0-rc.5');
  }
}

/**
 * Resolves a list of installable firmware versions based on the provided firmware package filenames.
 * Filters the list by the specified FWID or device, and optionally sorts the results by version in descending order.
 *
 * @param filenames An array of firmware package filenames to evaluate.
 * @param firmwareIdOrDevice The FWID or device object (with `hardwareDescription`, `stackInfo`, and optionally `integroVariant`) to check compatibility.
 * @param sortByVersion Optional flag to sort the resulting firmware list by version in descending order (default is `false`).
 * @returns A list of compatible firmware packages, each with its filename and version.
 */
export function resolveInstallableFirmwares(
  filenames: string[],
  firmwareIdOrDevice: string | Pick<Device, 'hardwareDescription' | 'stackInfo' | 'integroVariant'>,
  sortByVersion = false
): Firmware[] {
  const firmwares = filenames.reduce((firmwares: Firmware[], packageFilename: string) => {
    try {
      if (isFirmwarePackageFilenameCompatibleWithDevice(packageFilename, firmwareIdOrDevice)) {
        const version = extractFirmwareVersionFromFirmwarePackageFilename(packageFilename);
        firmwares.push({ packageFilename, version });
      }
    } catch (err) {}
    return firmwares;
  }, []);

  return sortByVersion ? firmwares.sort((a, b) => compareFirmwareVersionsDesc(a.version, b.version)) : firmwares;
}

/**
 * Compares two firmware versions so that the newest sorts first, for use as an `Array#sort` comparator.
 *
 * Firmware versions have to be compared per numeric component rather than as text: a major version of 10
 * or above precedes every 5.x.x version alphabetically, so comparing the strings ranks it as the oldest.
 *
 * Older firmware versions sometimes included invalid build metadata, which is removed before comparing,
 * the same way {@link firmwareVersionSatisfies} does. A version that is still not valid SemVer afterwards
 * sorts last, so one unparseable package filename cannot throw and take the ordering of the rest with it.
 */
export function compareFirmwareVersionsDesc(a: string, b: string): number {
  const versionA = valid(removeInvalidBuildMetadataFromFirmwareVersion(a), { loose: true });
  const versionB = valid(removeInvalidBuildMetadataFromFirmwareVersion(b), { loose: true });

  if (versionA === null && versionB === null) {
    return 0;
  }
  if (versionA === null) {
    return 1;
  }
  if (versionB === null) {
    return -1;
  }

  return rcompare(versionA, versionB, { loose: true });
}

/**
 * Parses the Integro firmware version and returns an object with the date, day, month, year, version.
 *
 * @param value The Integro firmware version.
 * @returns An object with the date, day, month, year, version.
 */
export function parseIntegroFirmwareVersion(value: number) {
  const str = value.toString();
  const day = str.slice(-2);
  const month = str.slice(-4, -2);
  const year = str.slice(-6, -4);
  const date = new Date(2000 + parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  const version = str.slice(-8, -6);
  return { date, day, month, value, version, year };
}

/**
 * Reads the firmware version out of a package filename, or returns an empty string if the filename does not
 * carry one. An empty string is not a valid version, so it sorts last in {@link compareFirmwareVersionsDesc}.
 */
function extractFirmwareVersionOrEmpty(filename: string): string {
  try {
    return extractFirmwareVersionFromFirmwarePackageFilename(filename);
  } catch (err) {
    return '';
  }
}

/**
 * Compares two firmware package filenames by the firmware version they carry so that the newest sorts first,
 * for use as an `Array#sort` comparator.
 *
 * The version is read with {@link extractFirmwareVersionFromFirmwarePackageFilename}, which knows both the new
 * and the old filename format and normalises the version, and compared with {@link compareFirmwareVersionsDesc}.
 * A filename carrying no readable version sorts last.
 */
export function compareFirmwarePackagesByVersionDesc(a: string, b: string): number {
  return compareFirmwareVersionsDesc(extractFirmwareVersionOrEmpty(a), extractFirmwareVersionOrEmpty(b));
}

/**
 * Maps firmware packages to filenames and buffers.
 *
 * @param packages
 * @returns
 */
export function mapPackagesToFilenameAndBuffer(packages: any[]): { filename: string; buffer: Buffer }[] {
  return packages.map((pkg) => ({
    filename: pkg.name.substring(pkg.name.lastIndexOf('/') + 1),
    buffer: pkg.buffer,
  }));
}
